import React from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import SEOSchema from './SEOSchema';

const SEO = ({ 
  page = 'home', 
  title = '', 
  description = '', 
  image = '',
  portfolioItems = []
}) => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const baseUrl = 'https://beckxfabian.be';
  
  // Métadonnées par défaut
  const defaultTitle = 'Fabian Beck - Développeur Web & Designer Graphique';
  const defaultDescription = t('about.description1');
  const defaultImage = `${baseUrl}/images/avatar-fabian-header.png`;

  const metaTitle = title || defaultTitle;
  const metaDescription = description || defaultDescription;
  const metaImage = image || defaultImage;

  // Titre de page spécifique
  const pageTitle = page === 'home' ? metaTitle : 
                   page === 'portfolio' ? `${t('nav.portfolio')} - ${defaultTitle}` :
                   page === 'about' ? `${t('nav.about')} - ${defaultTitle}` :
                   page === 'services' ? `${t('nav.services')} - ${defaultTitle}` :
                   page === 'contact' ? `${t('nav.contact')} - ${defaultTitle}` :
                   `${title} - ${defaultTitle}`;

  return (
    <>
      <Helmet>
        {/* Métadonnées de base */}
        <html lang={currentLang} />
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content="développeur web, designer graphique, UI/UX, React, JavaScript, Belgique, portfolio" />
        <meta name="author" content="Fabian Beck" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${baseUrl}${page !== 'home' ? `#${page}` : ''}`} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${baseUrl}${page !== 'home' ? `#${page}` : ''}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={metaImage} />
        <meta property="og:site_name" content="Fabian Beck Portfolio" />
        <meta property="og:locale" content={currentLang === 'fr' ? 'fr_BE' : 'en_US'} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${baseUrl}${page !== 'home' ? `#${page}` : ''}`} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={metaDescription} />
        <meta property="twitter:image" content={metaImage} />
        <meta property="twitter:creator" content="@beckxfabian" />

        {/* Métadonnées techniques */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />
        <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
        <meta name="format-detection" content="telephone=no" />
        
        {/* Favicon et icons */}
        <link rel="icon" type="image/x-icon" href="/favicon.ico" />
        <link rel="apple-touch-icon" sizes="180x180" href="/logo192.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />

        {/* Langues alternatives */}
        <link rel="alternate" hrefLang="fr" href={`${baseUrl}${page !== 'home' ? `#${page}` : ''}`} />
        <link rel="alternate" hrefLang="en" href={`${baseUrl}${page !== 'home' ? `#${page}` : ''}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${page !== 'home' ? `#${page}` : ''}`} />

        {/* Performance et sécurité */}
        <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
        <meta name="theme-color" content="#4db5ff" />
        
        {/* Portfolio spécifique */}
        {page === 'portfolio' && portfolioItems.length > 0 && (
          <meta name="description" content={`Découvrez mes ${portfolioItems.length} réalisations en développement web et design graphique`} />
        )}
      </Helmet>
      
      {/* Schémas JSON-LD */}
      <SEOSchema page={page} portfolioItems={portfolioItems} />
    </>
  );
};

export default SEO;
